import { applyTheme, cycleTheme, getPreferences } from './state.ts';
import {
  renderAccount,
  renderCategories,
  renderFeed,
  renderLegal,
  renderLibrary,
  renderLogin,
  renderOnboarding,
  renderPaper,
  renderPaperRequests,
  renderResetPassword,
  renderSearch,
  renderSettings,
  renderVerifyEmail,
} from './views.ts';
import type { Navigate } from './views.ts';

/**
 * ルーター兼アプリシェル。
 *
 * 履歴 API を使った通常のパス（/papers/... 等）で動く。
 * Worker 側の not_found_handling=single-page-application が index.html を返すため、
 * 直接アクセスやリロードでも同じ画面に着く。
 */

const view = document.getElementById('view');
const main = document.getElementById('main');
const searchInput = document.getElementById('global-search') as HTMLInputElement | null;
const themeToggle = document.getElementById('theme-toggle');

if (!view || !main) throw new Error('アプリの土台となる要素が見つかりません');

const viewRoot: HTMLElement = view;
const mainRoot: HTMLElement = main;

const THEME_LABEL: Record<string, string> = { light: 'ライト', dark: 'ダーク' };

const navigate: Navigate = (path, options = {}) => {
  if (options.replace) window.history.replaceState({}, '', path);
  else window.history.pushState({}, '', path);
  void route();
};

function setActiveTab(pathname: string): void {
  const tab =
    pathname === '/' || pathname === ''
      ? 'feed'
      : pathname.startsWith('/categories')
        ? 'categories'
        : pathname.startsWith('/search')
          ? 'search'
          : pathname.startsWith('/library')
            ? 'library'
            : pathname.startsWith('/settings')
              ? 'settings'
              : '';
  for (const item of document.querySelectorAll<HTMLElement>('.tabbar__item')) {
    if (item.dataset['tab'] === tab) item.setAttribute('aria-current', 'page');
    else item.removeAttribute('aria-current');
  }
}

async function route(): Promise<void> {
  const url = new URL(window.location.href);
  const path = url.pathname.replace(/\/+$/, '') || '/';
  setActiveTab(path);
  document.title =
    path === '/' ? 'PaperStock｜最新の論文' : `PaperStock｜${path.replace(/^\//, '').split('/')[0] ?? ''}`;

  const preferences = getPreferences();
  // 初期設定へ強制送還しない経路。
  // **決済とメールのリンクからの戻り先をここへ入れておくこと。**
  // 入れ忘れると、カード入力を終えた直後や確認メールを開いた直後に
  // クエリ文字列ごと初期設定へ飛ばされ、手続きが完了しない。
  const BYPASS_ONBOARDING = [
    '/onboarding',
    '/requests',
    '/account',
    '/login',
    '/account/verify',
    '/account/reset',
    // 法令表記は初期設定より先に読めなければならない。
    '/legal/tokushoho',
    '/legal/terms',
    '/legal/privacy',
  ];
  if (!preferences.onboarded && !BYPASS_ONBOARDING.includes(path)) {
    // 開こうとした画面（共有された論文リンクなど）を覚えておき、初期設定のあとにそこへ戻す。
    const next = path === '/' && url.search === '' ? '' : `?next=${encodeURIComponent(`${path}${url.search}`)}`;
    navigate(`/onboarding${next}`, { replace: true });
    return;
  }

  if (path === '/onboarding') {
    await renderOnboarding(viewRoot, navigate, url.searchParams);
  } else if (path === '/') {
    await renderFeed(viewRoot, navigate, url.searchParams);
  } else if (path === '/requests') {
    await renderPaperRequests(viewRoot);
  } else if (path === '/categories') {
    await renderCategories(viewRoot, navigate);
  } else if (path.startsWith('/papers/')) {
    await renderPaper(viewRoot, navigate, decodeURIComponent(path.slice('/papers/'.length)));
  } else if (path === '/search') {
    await renderSearch(viewRoot, navigate, url.searchParams);
  } else if (path === '/library') {
    renderLibrary(viewRoot, navigate);
  } else if (path === '/settings') {
    await renderSettings(viewRoot, navigate);
  } else if (path === '/login') {
    await renderLogin(viewRoot, navigate, url.searchParams);
  } else if (path === '/account') {
    await renderAccount(viewRoot, navigate, url.searchParams);
  } else if (path === '/account/verify') {
    await renderVerifyEmail(viewRoot, navigate, url.searchParams);
  } else if (path === '/account/reset') {
    await renderResetPassword(viewRoot, navigate, url.searchParams);
  } else if (path === '/legal/tokushoho') {
    await renderLegal(viewRoot, navigate, 'tokushoho');
  } else if (path === '/legal/terms') {
    await renderLegal(viewRoot, navigate, 'terms');
  } else if (path === '/legal/privacy') {
    await renderLegal(viewRoot, navigate, 'privacy');
  } else {
    viewRoot.innerHTML = '';
    const message = document.createElement('div');
    message.className = 'state';
    message.textContent = 'ページが見つかりません。';
    viewRoot.appendChild(message);
  }

  // 画面遷移後はフォーカスを本文へ移し、スクリーンリーダーで現在地が分かるようにする。
  mainRoot.focus({ preventScroll: true });
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
}

document.addEventListener('click', (event) => {
  const target = (event.target as HTMLElement | null)?.closest('a[data-link]');
  if (!(target instanceof HTMLAnchorElement)) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  const href = target.getAttribute('href');
  if (!href || href.startsWith('http')) return;
  event.preventDefault();
  navigate(href);
});

window.addEventListener('popstate', () => {
  void route();
});

searchInput?.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  const value = searchInput.value.trim();
  if (value.length > 0) navigate(`/search?q=${encodeURIComponent(value)}`);
});

themeToggle?.addEventListener('click', () => {
  const next = cycleTheme();
  themeToggle.setAttribute('title', `配色: ${THEME_LABEL[next] ?? next}`);
  themeToggle.setAttribute('aria-label', `配色を切り替える（現在: ${THEME_LABEL[next] ?? next}）`);
});

applyTheme(getPreferences().theme);
void route();
